import React, { useState } from 'react';
import { Send, X, AlertCircle, Users, MessageSquare, Mail, Clock, Eye } from 'lucide-react';
import {
  btnPrimary, btnSm, inp, neuShadowSm, cardWrap,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   BulkSendModal — queue an SMS or email blast to a filtered set of contacts.
   • Filter: contact_type, pipeline_stage, lead_source substring, tag substring
   • Variable substitution: {{first_name}} {{last_name}} {{organization}}
   • Dry run preview shows recipient count + first 5 names
   • Throttle staggers messages by N seconds (default 2s = 30/min)
   • All messages flow through the scheduler queue (migration 044) so a
     restart mid-blast doesn't double-send or lose progress
   ═══════════════════════════════════════════════════════════════════════════ */

const STAGES_DEFAULT = ['new', 'contacted', 'qualified', 'proposal', 'converted', 'lost', 'inactive'];

export default function BulkSendModal({ onClose, onSent }) {
  const [channel, setChannel] = useState('sms');
  const [filter, setFilter] = useState({
    contactType: '',
    pipelineStage: '',
    leadSource: '',
    tagContains: '',
  });
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [staggerSeconds, setStaggerSeconds] = useState(2);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const upFilter = (k, v) => setFilter(f => ({ ...f, [k]: v }));

  async function runPreview() {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const r = await window.zignagenixAPI.bulkSendCRMMessages({
        channel, filter, body, subject, dryRun: true,
      });
      setPreview(r);
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setPreviewing(false);
    }
  }

  async function send() {
    if (!body.trim()) { alert('Body is required.'); return; }
    if (!preview || !preview.success || preview.count === 0) {
      alert('Run preview first to confirm recipients.'); return;
    }
    if (!window.confirm(
      `Queue ${preview.count} ${channel.toUpperCase()} message${preview.count === 1 ? '' : 's'}?\n\n` +
      `Stagger: ${staggerSeconds}s between sends (${Math.round(60 / Math.max(1, staggerSeconds))}/min).\n` +
      `Estimated total time: ${formatMinutes(preview.count * staggerSeconds / 60)}.`
    )) return;
    setSending(true);
    try {
      const r = await window.zignagenixAPI.bulkSendCRMMessages({
        channel, filter, body, subject,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        staggerSeconds,
      });
      setResult(r);
      if (r.success) {
        // Wait a beat so the user can read the result, then close.
        setTimeout(() => { onSent?.(); onClose?.(); }, 2000);
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={16} color="#2E8B98" />
            <h2 style={modalTitle}>Bulk Send</h2>
          </div>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>

        {/* Channel switch */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <ChannelBtn icon={MessageSquare} label="SMS" value="sms" active={channel === 'sms'} onClick={() => setChannel('sms')} />
          <ChannelBtn icon={Mail} label="Email" value="email" active={channel === 'email'} onClick={() => setChannel('email')} />
        </div>

        {/* Filter card */}
        <div style={{ ...cardWrap, padding: 12, marginBottom: 12, background: '#FAFBFC' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 8 }}>
            Recipients
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
            <div>
              <Lbl>Contact type</Lbl>
              <select value={filter.contactType} onChange={e => upFilter('contactType', e.target.value)} style={inp}>
                <option value="">Any</option>
                <option value="prospect">Prospect</option>
                <option value="referral_physician">Referral physician</option>
                <option value="vendor">Vendor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Lbl>Pipeline stage</Lbl>
              <input value={filter.pipelineStage} onChange={e => upFilter('pipelineStage', e.target.value)}
                placeholder="e.g. Doctor Consult Completed (or leave blank)" style={inp}
                list="bulk-stage-options" />
              <datalist id="bulk-stage-options">
                {STAGES_DEFAULT.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <Lbl>Lead source contains</Lbl>
              <input value={filter.leadSource} onChange={e => upFilter('leadSource', e.target.value)}
                placeholder="e.g. facebook, podcast" style={inp} />
            </div>
            <div>
              <Lbl>Tag contains</Lbl>
              <input value={filter.tagContains} onChange={e => upFilter('tagContains', e.target.value)}
                placeholder="e.g. VIP, REACTIVATION" style={inp} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <button type="button" onClick={runPreview} disabled={previewing} style={btnSm}>
              <Eye size={12} /> {previewing ? 'Counting…' : 'Preview recipients'}
            </button>
            {preview?.success && (
              <div style={{ fontSize: 12, color: '#15803D', fontWeight: 700 }}>
                {preview.count} recipient{preview.count === 1 ? '' : 's'}
                {preview.preview?.length > 0 && (
                  <span style={{ fontWeight: 400, color: '#6C757D', marginLeft: 6 }}>
                    e.g. {preview.preview.slice(0, 3).map(p => `${p.first_name} ${p.last_name}`).join(', ')}
                    {preview.count > 3 && '…'}
                  </span>
                )}
              </div>
            )}
            {preview?.success && preview.count === 0 && (
              <div style={{ fontSize: 12, color: '#DC3545' }}>No matches</div>
            )}
          </div>
        </div>

        {/* Body card */}
        <div style={{ ...cardWrap, padding: 12, marginBottom: 12, background: '#FAFBFC' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 8 }}>
            Message
          </div>
          {channel === 'email' && (
            <div style={{ marginBottom: 8 }}>
              <Lbl>Subject</Lbl>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line" style={inp} />
            </div>
          )}
          <div>
            <Lbl>Body — supports {`{{first_name}} {{last_name}} {{organization}}`}</Lbl>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={6}
              placeholder={`Hi {{first_name}}, we have an opening this week — would you like to schedule?`}
              style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>

        {/* Schedule + throttle */}
        <div style={{ ...cardWrap, padding: 12, marginBottom: 14, background: '#FAFBFC' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 8 }}>
            Scheduling
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div>
              <Lbl>Start at <span style={{ fontWeight: 400, color: '#94A3B8' }}>· blank = ASAP</span></Lbl>
              <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} style={inp} />
            </div>
            <div>
              <Lbl>Stagger (sec)</Lbl>
              <input type="number" min="0" max="60" value={staggerSeconds}
                onChange={e => setStaggerSeconds(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)))}
                style={inp} />
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#6C757D', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={10} />
            {staggerSeconds > 0
              ? `${Math.round(60 / Math.max(1, staggerSeconds))} messages/minute`
              : 'No throttle (fastest, may hit Twilio rate limits)'}
          </div>
        </div>

        {/* Result */}
        {result && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 12,
            background: result.success ? '#dcfce7' : '#fef2f2',
            color: result.success ? '#166534' : '#991b1b', fontSize: 12,
          }}>
            {result.success ? (
              <span>
                ✓ Queued {result.queued} message{result.queued === 1 ? '' : 's'}
                {result.skipped > 0 && ` (${result.skipped} skipped)`}.
                First send at {result.first_send_at ? new Date(result.first_send_at).toLocaleString() : 'ASAP'}.
              </span>
            ) : (
              <span>✗ {result.error}</span>
            )}
          </div>
        )}

        {/* Compliance note */}
        {channel === 'sms' && (
          <div style={{
            padding: '8px 12px', borderRadius: 8, marginBottom: 12,
            background: '#FEF3C7', display: 'flex', alignItems: 'flex-start', gap: 8,
            fontSize: 11, color: '#7C2D12',
          }}>
            <AlertCircle size={14} color="#B45309" style={{ marginTop: 1, flexShrink: 0 }} />
            <div>
              <strong>SMS opt-out compliance:</strong> contacts who have replied STOP are
              automatically excluded. Bulk SMS still requires prior consent under A2P 10DLC —
              don't send to leads who haven't agreed to receive texts.
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !preview?.success || preview.count === 0 || !body.trim()}
            style={btnPrimary}
          >
            {sending ? 'Queuing…' : <><Send size={13} /> Queue {preview?.success ? preview.count : ''} message{preview?.count === 1 ? '' : 's'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChannelBtn({ icon: Icon, label, value, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      ...btnSm,
      flex: 1,
      background: active ? '#2E8B98' : '#F8F9FA',
      color: active ? '#fff' : '#6C757D',
      fontWeight: active ? 700 : 500,
      gap: 6,
      justifyContent: 'center',
    }}>
      <Icon size={13} /> {label}
    </button>
  );
}

const Lbl = ({ children }) => (
  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>
    {children}
  </label>
);
